import { useMemo, useState } from "react";
import { DetailHeader } from "@/components/layout/detail-header";
import { Button } from "@/components/ui/button";
import { useToast } from "@/contexts/toast-context";
import { Combobox } from "@/components/ui/combobox";
import { Table } from "@/components/ui/table";

type ServerHost = "adp.flatfile.com" | "qa.adp.flatfile.com";

export default function MappingsPage() {
  const { showError, showSuccess } = useToast();
  const [server, setServer] = useState<ServerHost>("adp.flatfile.com");
  const [token, setToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [programs, setPrograms] = useState<any[]>([]);
  const [search, setSearch] = useState(""); // rules search (header)
  const [programSearch, setProgramSearch] = useState(""); // sidebar mappings search
  const [selectedProgram, setSelectedProgram] = useState<any | null>(null);
  const pageSize = 15;
  const networkPageSize = 100;
  const [page, setPage] = useState(1);
  const filteredPrograms = useMemo(() => {
    const q = programSearch.trim().toLowerCase();
    const base = [...programs].sort((a: any, b: any) => {
      const ad = new Date(a.createdAt || 0).getTime();
      const bd = new Date(b.createdAt || 0).getTime();
      return bd - ad;
    });
    if (!q) return base;
    return base.filter((p: any) => JSON.stringify(p).toLowerCase().includes(q));
  }, [programs, programSearch]);

  const baseUrl = useMemo(() => `https://${server}/api/v1`, [server]);

  async function fetchPrograms() {
    if (!token) {
      showError("Please enter an API token.");
      return;
    }
    setIsLoading(true);
    try {
      const all: any[] = [];
      let pageNumber = 1;
      while (true) {
        const res = await fetch(`${baseUrl}/mapping?pageSize=${networkPageSize}&pageNumber=${pageNumber}`, {
          headers: {
            "Authorization": token.startsWith("Bearer ") ? token : `Bearer ${token}`,
            "X-Disable-Hooks": "true",
          },
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
        }
        const json = await res.json();
        const batch = Array.isArray(json?.data) ? json.data : [];
        if (batch.length === 0) break;
        all.push(...batch);
        if (batch.length < networkPageSize) break;
        pageNumber += 1;
      }
      setPrograms(all);
      setPage(1);
      if (all.length > 0) setSelectedProgram(all[0]);
      showSuccess(`Loaded ${all.length} mapping program(s).`);
    } catch (e: any) {
      showError("Couldn't load mapping programs. Check host and token, then try again.");
      setPrograms([]);
      setPage(1);
    } finally {
      setIsLoading(false);
    }
  }

  const rulesFiltered = useMemo(() => {
    const list = Array.isArray(selectedProgram?.rules) ? selectedProgram.rules : [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r: any) => {
      const hay = [
        r?.name,
        r?.type,
        r?.id,
        r?.acceptedBy,
        r?.createdBy,
        r?.acceptedAt,
        r?.createdAt,
        JSON.stringify(r?.config || {}),
        JSON.stringify(r?.metadata || {}),
      ].join("\n").toLowerCase();
      return hay.includes(q);
    });
  }, [selectedProgram, search]);

  return (
    <div className="flex flex-col h-full">
      <DetailHeader
        breadcrumb={[{ label: "Mappings" }]}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search rules..."
      />

      <div className="flex-1 overflow-hidden min-h-0 flex">
        {/* Left controls + programs list */}
        <aside className="w-80 min-w-80 max-w-80 border-r border-border flex flex-col">
          <div className="p-4 border-b border-border sticky top-0 bg-background z-10 space-y-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Server</label>
              <Combobox
                value={server}
                onChange={(v) => setServer((v as ServerHost) || "adp.flatfile.com")}
                options={[
                  { value: "adp.flatfile.com", label: "adp.flatfile.com", description: "Production" },
                  { value: "qa.adp.flatfile.com", label: "qa.adp.flatfile.com", description: "QA" },
                ]}
                placeholder="Select server..."
                className="text-xs"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Access token</label>
              <input
                className="w-full px-3 py-2.5 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                type="password"
                placeholder="Bearer ..."
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={fetchPrograms}
                disabled={!token || isLoading}
                size="sm"
                className="text-xs h-9 cursor-pointer"
              >
                {isLoading ? "Loading..." : "Load mappings"}
              </Button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="h-full flex items-center justify-center text-xs text-muted-foreground">Loading...</div>
            ) : programs.length === 0 ? (
              <div className="h-full flex text-xs justify-center items-center text-muted-foreground">No mapping programs</div>
            ) : (
              <div className="py-2 space-y-2">
                {/* Sidebar mapping search */}
                <div className="relative px-3">
                  <input
                    value={programSearch}
                    onChange={(e) => { setProgramSearch(e.target.value); setPage(1); }}
                    placeholder="Search mappings..."
                    className="w-full pl-8 pr-2 py-1.5 text-xs rounded border border-border bg-background"
                  />
                  <svg className="w-4 h-4 text-muted-foreground absolute left-5 top-1/2 -translate-y-1/2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                </div>

                {/* Client-side pagination controls */}
                <div className="flex items-center justify-between text-[11px] text-muted-foreground px-3">
                  <div>Page {page} of {Math.max(1, Math.ceil(filteredPrograms.length / pageSize))}</div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="h-7 text-[11px] px-2 cursor-pointer" variant="secondary" disabled={page <= 1} onClick={() => setPage((p: number) => Math.max(1, p - 1))}>Prev</Button>
                    <Button size="sm" className="h-7 text-[11px] px-2 cursor-pointer" variant="secondary" disabled={page >= Math.ceil(filteredPrograms.length / pageSize) || Math.ceil(filteredPrograms.length / pageSize) === 0} onClick={() => setPage((p: number) => p + 1)}>Next</Button>
                  </div>
                </div>

                {filteredPrograms.length === 0 ? (
                  <div className="h-full flex text-xs justify-center items-center text-muted-foreground">No mapping programs</div>
                ) : (
                  <div className="divide-y divide-border border-b border-t border-border">
                    {filteredPrograms
                      .slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize)
                      .map((p: any) => {
                  const isSelected = selectedProgram?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      className={`w-full text-left p-3 hover:bg-muted/50 cursor-pointer ${isSelected ? "bg-muted" : ""}`}
                      onClick={() => setSelectedProgram(p)}
                    >
                      <div className="text-xs font-medium truncate">{p.id}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{p.namespace} • {p.familyId} • Rules: {Array.isArray(p.rules) ? p.rules.length : 0}</div>
                    </button>
                  );
                })}
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>

        {/* Right pane: selected program details */}
        <section className="flex-1 min-h-0 overflow-auto">
          {!selectedProgram ? (
            <div className="h-full flex items-center justify-center text-xs text-muted-foreground">Select a mapping program to view rules</div>
          ) : (
            <div className="h-full flex flex-col">
              <div className="flex items-center justify-between pb-3 border-b border-border p-3">
                <div className="text-sm font-medium truncate">{selectedProgram.id}</div>
                <div className="text-[10px] text-muted-foreground truncate">{selectedProgram.namespace} • {selectedProgram.familyId}</div>
              </div>
              <div className="flex-1 min-h-0">
                <Table
                  data={rulesFiltered}
                  columns={[
                    { key: "name", header: "Name", width: 96, render: (r: any) => <span className="truncate block">{r.name || ""}</span> },
                    { key: "type", header: "Type", width: 36, render: (r: any) => <span className="truncate block">{r.type || ""}</span> },
                    { key: "confidence", header: "Conf.", width: 16, render: (r: any) => <span className="truncate block">{r.confidence ?? ""}</span>, className: "text-right" },
                    { key: "acceptedAt", header: "Accepted", width: 40, render: (r: any) => <span className="truncate block">{r.acceptedAt || "-"}</span> },
                    { key: "createdAt", header: "Created", width: 40, render: (r: any) => <span className="truncate block">{r.createdAt || "-"}</span> },
                    { key: "acceptedBy", header: "Accepted By", width: 40, render: (r: any) => <span className="truncate block">{r.acceptedBy || "-"}</span> },
                  ] as any}
                  fixedHeader
                  emptyMessage="No rules"
                  className="h-full"
                />
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}


